import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Person, ShoppingBag, ArrowForward } from '@mui/icons-material';

import { useAuth } from '../../../context/AuthContext';
import { useConfig } from '../../../utils/useConfig';

function Login() {
  const config = useConfig();
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (isAuthenticated && user) {
      const newPath = location.pathname.replace(/logowanie$/, 'zamówienie');
      navigate(newPath);
    }
  }, [isAuthenticated, user, navigate, location.pathname]);

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        when: 'beforeChildren',
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4 },
    },
  };

  return (
    <motion.div
      className="mx-auto bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden my-8"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <div className="bg-gray-50 px-6 py-4 border-b border-gray-100">
        <h1 className="text-xl font-medium text-gray-800">
          Finalizacja zakupu
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Wybierz sposób realizacji zamówienia
        </p>
      </div>

      <div className="px-6 py-8">
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 gap-6"
          variants={itemVariants}
        >
          {/* Opcja logowania */}
          <motion.div
            className="flex flex-col rounded-lg border border-gray-200 overflow-hidden hover:border-primary hover:shadow-md transition-all duration-200"
            whileHover={{
              y: -4,
              boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
            }}
          >
            <div
              className={`bg-${config.theme.mainColor}-50 px-5 py-4 border-b border-gray-200`}
            >
              <div className="flex items-center">
                <div
                  className={`flex-shrink-0 bg-${config.theme.mainColor}-100 p-2 rounded-full`}
                >
                  <Person className={`text-${config.theme.mainColor}-600`} />
                </div>
                <h2 className="ml-3 text-lg font-medium text-gray-800">
                  Mam już konto
                </h2>
              </div>
            </div>
            <div className="px-5 py-6 space-y-4 flex-grow">
              <div className="space-y-2">
                <p className="text-sm text-gray-600">
                  Zaloguj się, aby skorzystać ze swoich zapisanych adresów,
                  szybciej złożyć zamówienie i mieć historię zakupów.
                </p>
                <ul className="space-y-1">
                  {[
                    'Historia zamówień',
                    'Zapisane adresy',
                    'Szybsze zakupy',
                  ].map((item, i) => (
                    <li
                      key={i}
                      className="text-sm text-gray-600 flex items-center"
                    >
                      <svg
                        className="w-4 h-4 mr-2 text-green-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M5 13l4 4L19 7"
                        ></path>
                      </svg>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <motion.button
                className="w-full py-2.5 flex items-center justify-center rounded-md bg-primary text-white font-medium text-sm hover:bg-primary-dark transition-colors"
                onClick={() => {
                  const newPath = location.pathname.replace(
                    /logowanie$/,
                    'zamówienie',
                  );
                  navigate('/logowanie', {
                    state: {
                      from: {
                        pathname: newPath,
                      },
                    },
                  });
                }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Person fontSize="small" className="mr-2" />
                Zaloguj się
              </motion.button>
            </div>
          </motion.div>

          {/* Opcja kontynuacji jako gość */}
          <motion.div
            className="flex flex-col rounded-lg border border-gray-200 overflow-hidden hover:border-primary hover:shadow-md transition-all duration-200"
            whileHover={{
              y: -4,
              boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
            }}
          >
            <div className="bg-gray-50 px-5 py-4 border-b border-gray-200">
              <div className="flex items-center">
                <div className="flex-shrink-0 bg-gray-200 p-2 rounded-full">
                  <ShoppingBag className="text-gray-600" />
                </div>
                <h2 className="ml-3 text-lg font-medium text-gray-800">
                  Kontynuuj jako gość
                </h2>
              </div>
            </div>
            <div className="px-5 py-6 space-y-4 flex-grow">
              <div className="space-y-2">
                <p className="text-sm text-gray-600">
                  Złóż zamówienie bez konieczności zakładania konta. Podczas
                  finalizacji będziesz mieć możliwość utworzenia konta.
                </p>
                <ul className="space-y-1">
                  {[
                    'Szybki proces zakupu',
                    'Bez rejestracji',
                    'Możliwość utworzenia konta później',
                  ].map((item, i) => (
                    <li
                      key={i}
                      className="text-sm text-gray-600 flex items-center"
                    >
                      <svg
                        className="w-4 h-4 mr-2 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M5 13l4 4L19 7"
                        ></path>
                      </svg>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <motion.button
                className="w-full py-2.5 flex items-center justify-center rounded-md bg-gray-100 text-gray-800 font-medium text-sm hover:bg-gray-200 transition-colors"
                onClick={() => {
                  const newPath = location.pathname.replace(
                    /logowanie$/,
                    'zamówienie',
                  );
                  navigate(newPath);
                }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <ArrowForward fontSize="small" className="mr-2" />
                Kontynuuj jako gość
              </motion.button>
            </div>
          </motion.div>
        </motion.div>

        <motion.p
          className="text-center text-xs text-gray-500 mt-8"
          variants={itemVariants}
        >
          Twoje dane są zawsze bezpieczne i chronione zgodnie z polityką
          prywatności.
        </motion.p>
      </div>
    </motion.div>
  );
}

export default Login;
